import Immutable from 'immutable';

import {
  isDomainIe,
  isCurrencyEur,
  penceToPounds,
  readCookie,
} from 'utils/utils';
import {
  getInsuranceDescriptionByType,
} from 'utils/book';
import dates from 'utils/dates';
import { getUserClassArray } from 'utils/dataLayerHelpers';
import LHX from 'utils/LHX';

const isEnablePurchaseTracking = state => state.bookState.getIn(['mode']) !== 'agent' && LHX.environment === 'prod' && readCookie('lastBookingRef') !== state.bookState.getIn(['status', 'booking_reference']);
const getBaseStateQueryProps = state => state.baseState.getIn(['queryState', 'props'], Immutable.Map());
const getBookingReference = state => state.bookState.getIn(['status', 'booking_reference']);
const getCheckInDate = state => state.bookState.getIn(['data', 'check_in_date']);
const getCity = state => state.bookState.getIn(['entities', 'passengers', '0', 'city'], '');
const getCountryCode = () => isDomainIe() ? 'IE' : 'UK';
const getCounty = state => state.bookState.getIn(['entities', 'passengers', '0', 'county']);
const getCurrency = () => isCurrencyEur() ? 'EUR' : 'GBP';
const getCurrencySymbol = () => isCurrencyEur() ? '€' : '£';
const getCheapestOfferOfDay = state => state.appState.get('selectedOffer');
const getDepartureCode = (state, bookType) => bookType !== 'hotel-only' ? state.bookState.getIn(['data', 'flight', 'outbound', 'deptCode']) : '';
const getDepartureDate = state => state.bookState.getIn(['data', 'flight', 'outbound', 'deptDate']);
const getDestinationAirportCode = state => state.bookState.getIn(['data', 'flight', 'outbound', 'arrvCode']);
const getDestinationAirportName = state => state.bookState.getIn(['data', 'flight', 'outbound', 'arrvName']);
const getExpectedRevenue = state => state.bookState.getIn(['tracking', 'expected_revenue']) / 100;
const getHoldLuggagePrice = state => state.bookState.getIn(['priceBreakdown', 'baggagePrice']);
const getHotelDetailsMasterId = state => state.hotelDetails.get('masterId', '');
const getHotelLargeImage = state => state.bookState.getIn(['data', 'hotelLargeImageUrl']);
const getHotelSmallImage = state => state.bookState.getIn(['data', 'hotelSmallImageUrl']);
const getHotelStarRating = state => state.bookState.getIn(['data', 'star_rating']);
const getHotelName = state => state.bookState.getIn(['data', 'hotel_name']);
const getInboundDepartureDate = state => state.bookState.getIn(['data', 'flight', 'inbound', 'deptDate']);
const getInboundDepartureTime = state => state.bookState.getIn(['data', 'flight', 'inbound', 'deptTime']);
const getInboundAirline = state => state.bookState.getIn(['data', 'flight', 'inbound', 'airline']);
const getInboundArrivalDate = state => state.bookState.getIn(['data', 'flight', 'inbound', 'arrvDate']);
const getInboundArrivalTime = state => state.bookState.getIn(['data', 'flight', 'inbound', 'arrvTime']);
const getInsuranceTypeId = state => state.bookState.getIn(['booking_info', 'selection', 'insurance_selected']);
const getInsuranceType = state => state.bookState.getIn(['entities', 'insurance', `${getInsuranceTypeId(state)}`, 'coverType']);
const getInsurancePrice = state => state.bookState.getIn(['priceBreakdown', 'insurancePrice']);
const getLeadPassengerID = state => state.bookState.getIn(['booking_info', 'leadPassengerId']).toString();
const getLeadPassengerDateOfBirthDay = state => state.bookState.getIn(['entities', 'passengers', getLeadPassengerID(state), 'dateOfBirthDay']);
const getLeadPassengerDateOfBirthMonth = state => state.bookState.getIn(['entities', 'passengers', getLeadPassengerID(state), 'dateOfBirthMonth']);
const getLeadPassengerDateOfBirthYear = state => state.bookState.getIn(['entities', 'passengers', getLeadPassengerID(state), 'dateOfBirthYear']);
const getInsuranceDescription = state => getInsuranceDescriptionByType(getInsuranceType(state), getCurrencySymbol());
const getLeadPassengerEmail = state => state.bookState.getIn(['entities', 'passengers', getLeadPassengerID(state), 'email']);
const getLeadPassengerSubscribe = state => state.bookState.getIn(['entities', 'passengers', getLeadPassengerID(state), 'subscribe'], false).toString();
const getLeadPassengerFirstName = state => state.bookState.getIn(['entities', 'passengers', getLeadPassengerID(state), 'firstName']);
const getLeadPassengerLastName = state => state.bookState.getIn(['entities', 'passengers', getLeadPassengerID(state), 'lastName']);
const getLeadPassengerTitle = state => state.bookState.getIn(['entities', 'passengers', getLeadPassengerID(state), 'title']);
const getMasterId = state => state.bookState.getIn(['data', 'master_id']);
const getNumberOfAdults = state => state.bookState.getIn(['data', 'partyInfo', 'numberOfAdults']);
const getNumberOfChargeablePassengers = state => state.bookState.getIn(['data', 'partyInfo', 'numberOfChargeablePassengers']);
const getNumberOfChildren = state => state.bookState.getIn(['data', 'partyInfo', 'numberOfChildren']);
const getNumberOfHoldLuggage = state => (state.bookState.getIn(['booking_info', 'selection', 'extras', 'baggage']) || Immutable.List()).size;
const getNumberOfNights = state => state.bookState.getIn(['data', 'num_nights']);
const getNumberOfRooms = state => (state.bookState.getIn(['booking_info', 'selection', 'room_selected']) || Immutable.List()).size;
const getOriginAirportCode = state => state.bookState.getIn(['data', 'flight', 'outbound', 'deptCode']);
const getOriginAirportName = state => state.bookState.getIn(['data', 'flight', 'outbound', 'deptName']);
const getOutboundAirline = state => state.bookState.getIn(['data', 'flight', 'outbound', 'airline']);
const getOutboundDepartureDate = state => state.bookState.getIn(['data', 'flight', 'outbound', 'deptDate']);
const getOutboundDepartureTime = state => state.bookState.getIn(['data', 'flight', 'outbound', 'deptTime']);
const getOutboundArrivalDate = state => state.bookState.getIn(['data', 'flight', 'outbound', 'arrvDate']);
const getOutboundArrivalTime = state => state.bookState.getIn(['data', 'flight', 'outbound', 'arrvTime']);
const getPaymentPlan = state => state.bookState.getIn(['booking_info', 'payment_details', 'deposit']);
const getPhgCommissionTier = state => state.bookState.getIn(['tracking', 'phg_commission_tiers']);
const getResort = state => state.bookState.getIn(['data', 'destination']);
const getTotalNow = state => state.bookState.getIn(['priceBreakdown', 'totalPrice']);
const getTotalWas = state => state.bookState.getIn(['priceBreakdown', 'totalWasPrice']);
const getTransferPrice = state => state.bookState.getIn(['priceBreakdown', 'transferPrice']);
const getTransferTypeId = state => state.bookState.getIn(['booking_info', 'selection', 'transfer_selected']);
const getTransferType = state => state.bookState.getIn(['entities', 'transfers', `${getTransferTypeId(state)}`, 'type']);
const getParkingPrice = state => state.bookState.getIn(['priceBreakdown', 'parkingPrice']);
const getParkingTypeId = state => state.bookState.getIn(['booking_info', 'selection', 'parking_selected']);
const getParkingType = state => state.bookState.getIn(['entities', 'parking', `${getParkingTypeId(state)}`, 'type']);
const getTransactionAffiliation = bookType => bookType === 'hotel-only' ? 'HOTEL_ONLY' : 'DYN_PACKAGE';
const getTransactionCurrency = getCurrencySymbol;
const getTransactionId = getBookingReference;
const getTransactionShipping = () => '';
const getTransactionTax = () => '';
const getTransactionTotal = getExpectedRevenue;
const getTransactionProducts = state => ([{
  id: `${getMasterId(state)}`,
  name: state.bookState.getIn(['data', 'hotel_name']),
  sku: `${getMasterId(state)}`,
  category: state.bookState.getIn(['tracking', 'resort']),
  price: getTransactionTotal(state),
  quantity: '1',
}]);
const getWebSellingPrice = state => state.bookState.getIn(['tracking', 'web_selling_price']) / 100;

const getCheckOutDate = state => dates.format(dates.addDays(getNumberOfNights(state), dates.isoStringToDate(getCheckInDate(state))), 'YYYY-MM-DD');
const getPricePerPerson = state => getTotalNow(state) / getNumberOfChargeablePassengers(state);

const getPriceTrackerFullName = state => state.priceTracker.get('fullName');
const getPriceTrackerEmail = state => state.priceTracker.get('email');
const getPriceTrackerNewsletterSubscription = state => state.priceTracker.get('newsletterSubscription');

const getShortlistEmail = state => state.shortlist.get('mainRecipientEmail');
const getShortlistNewsletterSubscription = state => state.shortlist.get('newsletterSubscription');

const getSelectedBoardBasis = (state) => {
  const boardBasisCode = state.bookState.getIn(['entities', 'rooms', `${[state.bookState.getIn(['booking_info', 'selection', 'room_selected', '0'])]}`, 'board_basis']);
  return state.bookState.getIn(['data', 'board_basis_config', boardBasisCode, 'name']);
};

const getLeadPassengerDateOfBirth = (state) => {
  // this is currently in the format dd/mm/yyyy
  const dateOfBirth = [];
  dateOfBirth[0] = `0${getLeadPassengerDateOfBirthDay(state)}`.substr(-2);
  dateOfBirth[1] = `0${getLeadPassengerDateOfBirthMonth(state)}`.substr(-2);
  dateOfBirth[2] = getLeadPassengerDateOfBirthYear(state);
  return dateOfBirth.join('/');
};

function getRoomTypeAndPrice(state, roomNumber) {
  const roomId = state.bookState.getIn(['booking_info', 'selection', 'room_selected', roomNumber]);
  let description;
  let price;
  let roomData = {};

  const id = state.bookState.getIn(['entities', 'roomRestrictions', `${roomId}`, 'roomId']);
  description = state.bookState.getIn(['entities', 'roomPricingOptions', `${roomId}`, 'description']);
  price = penceToPounds(state.bookState.getIn(['entities', 'roomRestrictions', `${id}`, 'pricing', 'webPrice']));

  if (description && price) {
    roomData = {
      [`room${roomNumber}Type`]: description,
      [`room${roomNumber}Price`]: price,
    };
  }

  return roomData;
}

export const GTM_EVENT_NAMES = {
  TRACK_TRANS: 'TRACK_TRANS',
  TRACK_PURCHASE: 'TRACK_PURCHASE',
  TRACK_CONF: 'TRACK_CONF',
  TRACK_LEAD_PASSENGER: 'TRACK_LEAD_PASSENGER',
  TRACK_PRICE_TRACKER: 'TRACK_PRICE_TRACKER',
  TRACK_SHORTLIST_SUBMIT: 'TRACK_SHORTLIST_SUBMIT',
  TRACK_PANDA: 'TRACK_PANDA',
};

export function getTrackPandaEvent(state) {
  const cheapestOfferOfDay = getCheapestOfferOfDay(state);
  const baseQuery = getBaseStateQueryProps(state);

  const data = {
    codeBase: 'react',
    userClass: getUserClassArray(LHX.userClass),
    page: {
      pageInfo: {
        search: {
          hotels: {
            adults: baseQuery.getIn(['nAdt', 'value']),
            children: baseQuery.getIn(['cdn', 'arrayValue'], Immutable.List()).size,
            nights: baseQuery.getIn(['duration', 'value']),
            rooms: baseQuery.get('rooms', Immutable.List()).size,
          },
          customer: {
            brand: getCountryCode(),
          },
        },
      },
    },
    product: [{
      productInfo: {
        productID: String(getHotelDetailsMasterId(state)),
      },
    }],
    transaction: {
      total: {
        priceBookTodayFor: '',
      },
      currency: getCurrencySymbol(state),
    },
    event: 'trackPanda',
  };

  if (cheapestOfferOfDay) {
    data.page.pageInfo.search.hotels.checkIn = dates.format(cheapestOfferOfDay.get('arrivalDate'), 'YYYY-MM-DD');
    data.page.pageInfo.search.hotels.boardBasis = String(cheapestOfferOfDay.get('boardBasis'));
    data.transaction.total.transactionTotal = cheapestOfferOfDay.get('totalDiscountPrice');
    data.transaction.total.priceWas = cheapestOfferOfDay.get('retailPrice');
    data.transaction.total.transactionPerPerson = cheapestOfferOfDay.get('price');
  }

  return data;
}

export function getTrackTransEvent(state, shortRef, bookType) {
  return {
    brand: getCountryCode(),
    transactionId: getTransactionId(state),
    transactionAffiliation: getTransactionAffiliation(bookType),
    transactionTotal: String(getTransactionTotal(state)),
    transactionCurrency: getTransactionCurrency(state),
    transactionTax: getTransactionTax(),
    transactionShipping: getTransactionShipping(),
    transactionProducts: getTransactionProducts(state),
    event: 'trackTrans',
  };
}

export function getTrackPurchaseEvent(state, shortRef, bookType) {
  return {
    enable_purchase_tracking: isEnablePurchaseTracking(state) ? 'True' : 'False',
    tracking_reference: getBookingReference(state),
    shortref: shortRef,
    expected_revenue: String(getExpectedRevenue(state)),
    web_selling_price: String(getWebSellingPrice(state)),
    masterId: String(getMasterId(state)),
    num_children: getNumberOfChildren(state),
    num_adults: getNumberOfAdults(state),
    num_nights: getNumberOfNights(state),
    checkin_date: getDepartureDate(state),
    dept_code: getDepartureCode(state, bookType),
    destination_urlencode: encodeURIComponent(state.bookState.getIn(['data', 'destination'])),
    phg_commission_tier: getPhgCommissionTier(state),
    event: 'trackPurchase',
  };
}

export function getTrackLeadPassengerEvent(state) {
  return {
    leadPassengerTitle: getLeadPassengerTitle(state),
    leadPassengerFirstName: getLeadPassengerFirstName(state),
    leadPassengerLastName: getLeadPassengerLastName(state),
    leadPassengerEmail: getLeadPassengerEmail(state),
    leadPassengerSubscribe: getLeadPassengerSubscribe(state),
    leadPassengerDateOfBirth: getLeadPassengerDateOfBirth(state),
    event: 'trackLeadPassenger',
  };
}

export function getTrackPriceTrackerEvent(state) {
  return {
    priceTrackerFullName: getPriceTrackerFullName(state),
    priceTrackerEmail: getPriceTrackerEmail(state),
    priceTrackerNewsletterSubscription: getPriceTrackerNewsletterSubscription(state),
    event: 'trackPriceTracker',
  };
}

export function getTrackShortlistSubmitEvent(state) {
  return {
    shortlistEmail: getShortlistEmail(state),
    shortlistNewsletterSubscription: getShortlistNewsletterSubscription(state),
    event: 'trackShortlistSubmit',
  };
}

export function getTrackConfirmationEvent(state, shortRef, bookType) {
  return {
    event: 'trackConf',
    codeBase: 'react',
    confirmationData: {
      ...getRoomTypeAndPrice(state, 0),
      ...getRoomTypeAndPrice(state, 1),
      ...getRoomTypeAndPrice(state, 2),
      ...getRoomTypeAndPrice(state, 3),
      affiliationId: readCookie('lvh_ch'),
      boardBasis: getSelectedBoardBasis(state),
      checkInDate: getCheckInDate(state),
      checkOutDate: getCheckOutDate(state),
      city: getCity(state).replace(/,.*/, ''),
      county: getCounty(state),
      currency: getCurrency(),
      destinationAirportCode: getDestinationAirportCode(state),
      destinationAirportName: getDestinationAirportName(state),
      expected_revenue: String(getExpectedRevenue(state)),
      hotelLargeImage: getHotelLargeImage(state),
      hotelName: getHotelName(state),
      hotelSmallImage: getHotelSmallImage(state),
      hotelStarRating: getHotelStarRating(state),
      inboundAirline: getInboundAirline(state),
      inboundArrvDate: `${getInboundArrivalDate(state)}`,
      inboundArrvDateTime: `${getInboundArrivalDate(state)} ${getInboundArrivalTime(state)}`,
      inboundDeptDateTime: `${getInboundDepartureDate(state)} ${getInboundDepartureTime(state)}`,
      insuranceDescription: getInsuranceDescription(state),
      insurancePrice: penceToPounds(getInsurancePrice(state)),
      insuranceType: getInsuranceType(state),
      luggagePrice: penceToPounds(getHoldLuggagePrice(state)),
      lvhId: readCookie('lvhid'),
      masterId: getMasterId(state),
      numberOfAdults: getNumberOfAdults(state),
      numberOfChildren: getNumberOfChildren(state),
      numberOfHoldLuggage: getNumberOfHoldLuggage(state),
      numberOfNights: getNumberOfNights(state),
      numberOfRooms: getNumberOfRooms(state),
      originAirportCode: getOriginAirportCode(state),
      originAirportName: getOriginAirportName(state),
      outboundAirline: getOutboundAirline(state),
      outboundArrvDateTime: `${getOutboundArrivalDate(state)} ${getOutboundArrivalTime(state)}`,
      outboundDeptDate: `${getOutboundDepartureDate(state)}`,
      outboundDeptDateTime: `${getOutboundDepartureDate(state)} ${getOutboundDepartureTime(state)}`,
      paymentPlan: getPaymentPlan(state),
      parkingPrice: penceToPounds(getParkingPrice(state)),
      parkingType: getParkingType(state),
      pricePerPerson: penceToPounds(getPricePerPerson(state)),
      resort: getResort(state),
      total: penceToPounds(getTotalNow(state)),
      totalNow: penceToPounds(getTotalNow(state)),
      totalWas: penceToPounds(getTotalWas(state)),
      transactionId: getTransactionId(state),
      transactionTotal: String(getTransactionTotal(state)),
      transactionAffiliation: getTransactionAffiliation(bookType),
      transferPrice: penceToPounds(getTransferPrice(state)),
      transferType: getTransferType(state),
    },
    userClass: getUserClassArray(LHX.userClass),
  };
}

const eventFactories = {
  [GTM_EVENT_NAMES.TRACK_TRANS]: getTrackTransEvent,
  [GTM_EVENT_NAMES.TRACK_PURCHASE]: getTrackPurchaseEvent,
  [GTM_EVENT_NAMES.TRACK_CONF]: getTrackConfirmationEvent,
  [GTM_EVENT_NAMES.TRACK_LEAD_PASSENGER]: getTrackLeadPassengerEvent,
  [GTM_EVENT_NAMES.TRACK_PRICE_TRACKER]: getTrackPriceTrackerEvent,
  [GTM_EVENT_NAMES.TRACK_SHORTLIST_SUBMIT]: getTrackShortlistSubmitEvent,
  [GTM_EVENT_NAMES.TRACK_PANDA]: getTrackPandaEvent,
};

export function fireEvent(eventName, state, shortRef, bookType) {
  if (eventFactories[eventName]) {
    const event = eventFactories[eventName](state, shortRef, bookType);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }
}



/** WEBPACK FOOTER **
 ** ./src/utils/gtm.js
 **/